import { lazy, Suspense } from "react";
import { useParams, type RouteObject } from "react-router-dom";
import { ApiState } from "@/shared/components/ApiState";
import { useAppShell } from "@/shell/app-shell-context";
import { useRequireCompany } from "@/shared/hooks/useRequireCompany";
import { hasRoleAtLeast } from "@/shared/utils/roles";
import { versaEnabled } from "./config";

// As telas do Versa (editor, Chart.js, leitor de planilha) vêm num pacote
// à parte, baixado só por quem abre o Versa: sem isso, todo usuário do
// Reputaition carregaria ~1 MB a mais na primeira tela.
const VersaReportsPage = lazy(() => import("./VersaReportsPage").then(module => ({ default: module.VersaReportsPage })));
const VersaReportPage = lazy(() => import("./VersaReportPage").then(module => ({ default: module.VersaReportPage })));
const VersaSharePage = lazy(() => import("./share/VersaSharePage").then(module => ({ default: module.VersaSharePage })));
const ShortLinksPage = lazy(() => import("./sentinel/ShortLinksPage").then(module => ({ default: module.ShortLinksPage })));
const LeavingPage = lazy(() => import("./sentinel/LeavingPage").then(module => ({ default: module.LeavingPage })));

const loading = <ApiState kind="loading">Carregando o Versa…</ApiState>;

/**
 * A rota da lista vive DENTRO da empresa (`/empresas/:companyId/versa`):
 * o `X-Company-Id` que o client manda sai da URL, então fora desse
 * caminho a API não saberia de qual empresa são os relatórios.
 */
function VersaReportsRoute() {
  const { token, profile } = useAppShell();
  const company = useRequireCompany();
  if (!company) return null;
  // Copiar modelo de outra empresa (2026-11-03): Máster e Administrador,
  // das empresas que o `/me` já lhes dá (o Máster recebe todas). O
  // servidor confere de novo o acesso a cada uma.
  const copySources = hasRoleAtLeast(profile, "administrator")
    ? profile.companies.filter(other => other.id !== company.id && other.is_active).map(other => ({ id: other.id, name: other.name }))
    : undefined;
  return (
    <Suspense fallback={loading}>
      <VersaReportsPage token={token} copySources={copySources} />
    </Suspense>
  );
}

function VersaReportRoute() {
  const { token } = useAppShell();
  const company = useRequireCompany();
  const dashboardId = Number(useParams().dashboardId);
  if (!company) return null;
  if (!Number.isInteger(dashboardId) || dashboardId <= 0) return null;
  // `key`: trocar de relatório pela URL remonta a página, sem herdar o
  // estado (bases, envio em andamento) do anterior.
  return (
    <Suspense fallback={loading}>
      <VersaReportPage key={dashboardId} token={token} dashboardId={dashboardId} clientName={company.name} />
    </Suspense>
  );
}

/** Os links curtos de quem está logado (Sentinela, 2026-11). Sem o
 * Sentinela ligado no back, a página mostra o erro da API. */
function ShortLinksRoute() {
  const { token } = useAppShell();
  const company = useRequireCompany();
  if (!company) return null;
  return (
    <Suspense fallback={loading}>
      <ShortLinksPage token={token} />
    </Suspense>
  );
}

/**
 * Rotas filhas de `empresas/:companyId`. Ponto de encaixe: o roteador
 * espalha esta lista (vazia com o Versa desligado).
 */
export const versaCompanyRoutes: RouteObject[] = versaEnabled
  ? [
      { path: "versa", element: <VersaReportsRoute /> },
      { path: "versa/links", element: <ShortLinksRoute /> },
      { path: "versa/:dashboardId", element: <VersaReportRoute /> },
    ]
  : [];

/**
 * O link do cliente (`/share/versa/:code`): rota de topo, FORA da casca do
 * Reputaition — sem login, sem menu, sem empresa na URL. Ponto de encaixe:
 * o roteador espalha esta lista no topo (vazia com o Versa desligado).
 */
export const versaPublicRoutes: RouteObject[] = versaEnabled
  ? [
      {
        path: "/share/versa/:code",
        element: (
          <Suspense fallback={<ApiState kind="loading">Abrindo o relatório…</ApiState>}>
            <VersaSharePage />
          </Suspense>
        ),
      },
      {
        // O link curto do Sentinela: mostra o destino antes de levar.
        path: "/l/:code",
        element: (
          <Suspense fallback={<ApiState kind="loading">Abrindo o link…</ApiState>}>
            <LeavingPage />
          </Suspense>
        ),
      },
    ]
  : [];
